import { SignalBuffer } from "./signal-buffer";
import { SignalBufferColumnModeProvider } from "./signal-buffer-column-mode-provider";
import { SignalBufferLineModeProvider } from "./signal-buffer-line-mode-provider";

export class ColumnLineSignalBuffer extends SignalBuffer {
  constructor(settings) {
    super(settings);
  }

  init() {
    this.dataBuffer = [];
    this.dataIndex = -1;
    if (!this.settings.isDataSync) {
      this.dataBuffer.length = this.settings.dataBufferSize;
      this.dataBuffer.fill(0);
    }
    this.createBuffers();
  }

  // freq in Hz
  setFreq(freq) {
    this.settings.freq = freq;
    this.createBuffers();
  }

  // scaleX in mm/s
  setScaleX(newScaleX) {
    this.settings.scaleX = newScaleX;
    this.createBuffers();
  }

  setScaleY(newScaleY) {
    this.settings.scaleY = newScaleY;
    this.updatePixelsPointsValues();
  }

  // plotSize: { width: 0, height: 0}, windowSize: { width: 0, height: 0 }
  updateWindowSize(plotSize, windowSize) {
    this.settings.plotSize = plotSize;
    this.settings.windowSize = windowSize;
    this.createBuffers();
  }

  reset() {
    this.createBuffers();
  }

  getFirstNotNullGraphicDataIndex(graphicIndex) {
    let graphicDataIndex = this.getFirstGraphicDataIndex(graphicIndex);
    if (graphicDataIndex === undefined) {
      return undefined;
    }
    for (let i = graphicDataIndex; i < this.graphicDataBuffer.length && this.graphicDataBuffer[i].graphicIndex == graphicIndex; i++) {
      if (this.getData(i) != null) {
        return i;
      }
    }
    return undefined;
  }

  getAmpl(graphicIndex) {
    let signalIndex = this.getFirstNotNullGraphicDataIndex(graphicIndex);
    if (signalIndex === undefined) {
      return undefined;
    }
    return this.getData(signalIndex);
  }

  getMinMax(beginIndex, endIndex) {
    if (beginIndex < 0 || endIndex < beginIndex || endIndex >= this.graphicDataBuffer.length) {
      return undefined;
    }
    let minValue = this.getData(beginIndex);
    let maxValue = this.getData(beginIndex);

    for (let i = beginIndex + 1; i < this.graphicDataBuffer.length; i++) {
      const data = this.getData(i);
      if (data != null) {
        if (minValue == null || data < minValue) {
          minValue = data;
        }
        if (maxValue == null || data > maxValue) {
          maxValue = data;
        }
      }
    }
    return {
      min: minValue,
      max: maxValue
    };
  }

  getSourceData(graphicDataIndex) {
    if (graphicDataIndex < 0 || graphicDataIndex >= this.graphicDataBuffer.length) {
      return undefined;
    }
    const dataIndex = this.graphicDataBuffer[graphicDataIndex].dataIndex;
    if (dataIndex < 0 || dataIndex >= this.dataBuffer.length) {
      return undefined;
    }
    return this.dataBuffer[dataIndex];
  }

  getData(graphicDataIndex) {
    return this.getSourceData(graphicDataIndex);
  }

  isNextPointGraphicBegin() {
    return (this.graphicIndex + 1) % this.graphicBufferLength == 0;
  }

  getCurrPointX() {
    return this.graphicIndex < 0 ? 0 : this.pointBuffer[this.graphicIndex * 4];
  }

  getCurrPoint() {
    return this.graphicDataIndex < 0 ? undefined : this.graphicDataBuffer[this.graphicDataIndex];
  }

  getCurrPointIndex() {
    return this.graphicDataIndex < 0 ? 0 : this.graphicDataIndex;
  }

  // data in UserUnits
  convertValueToPixels(data) {
    return data * this.settings.valuePixelConvertKoef * this.settings.scaleY;
  }

  updatePointsValues() {
    this.updatePixelsPointsValues();
  }

  updatePixelsPointsValues() {
    this.signalBufferModeProvider.updatePixelsPointsValues();
  }

  createBuffers() {
    this.notNullSectors = [];
    const pointCounter = this.settings.pointCounterCreator(this);
    const pointCount = pointCounter.calculatePointCount();

    const graphicDataBufferLength = Math.ceil(pointCount / this.settings.scaleX); // in points
    const plotWidthInPx = Math.ceil(this.settings.plotSize.width);
    this.graphicDataBufferLength = graphicDataBufferLength;

    this.isColumnMode = graphicDataBufferLength >= plotWidthInPx;
    this.graphicDataBuffer = [];
    this.actualGraphicBufferLength = 0;
    this.actualGraphicDataBufferLength = 0;
    this.isoline = null;

    if (this.isColumnMode) {
      this.signalBufferModeProvider = new SignalBufferColumnModeProvider(this);
      this.signalBufferModeProvider.createPointBuffer(plotWidthInPx, graphicDataBufferLength);
    } else {
      
      this.signalBufferModeProvider = new SignalBufferLineModeProvider(this);
      this.signalBufferModeProvider.createPointBuffer(graphicDataBufferLength, pointCount);
    }
    this.graphicDataBuffer.length = graphicDataBufferLength;
    if (this.settings.isDataSync) {
      this.dataBuffer = [];
      this.dataBuffer.length = graphicDataBufferLength;
      for (let dataIndex = 0; dataIndex < graphicDataBufferLength; ++dataIndex) {
        this.graphicDataBuffer[dataIndex] = {
          dataIndex: dataIndex,
          graphicIndex: -1
        };
        this.dataBuffer[dataIndex] = 0;
      }
      this.dataIndex = -1;
    } else {
      for (let dataIndex = 0; dataIndex < graphicDataBufferLength; ++dataIndex) {
        this.graphicDataBuffer[dataIndex] = {
          dataIndex: -1,
          graphicIndex: -1
        };
      }
    }

    this.graphicDataIndex = -1;
    this.graphicIndex = -1;
    this.lastValidGraphicIndex = -1;
    this.startGraphicIndex = 0; // associate graphicDataIndex with start graphicBuffer's position
    this.startGraphicDataIndex = 0; // graphicDataIndex first point with graphicIndex == startGraphicIndex
  }

  // add data without valid flag check
  // data in user units
  putData(data) {
    ++this.dataIndex;
    if (this.dataIndex == this.dataBuffer.length) {
      this.dataIndex = 0;
    }
    if (data == null || isNaN(data)) {
      data = null;
    }
    this.signalBufferModeProvider.putData(data);
    this.dataBuffer[this.dataIndex] = data;
    this.graphicDataBuffer[this.graphicDataIndex] = {
      dataIndex: this.dataIndex,
      graphicIndex: this.graphicIndex
    };
    if (this.actualGraphicBufferLength < this.graphicBufferLength) {
      this.actualGraphicBufferLength = this.graphicIndex + 1;
      if (this.actualGraphicBufferLength > this.graphicBufferLength) {
        this.actualGraphicBufferLength = this.graphicBufferLength;
      }
    }
    if (this.actualGraphicDataBufferLength < this.graphicDataBufferLength) {
      ++this.actualGraphicDataBufferLength;
    }
  }

  _pushIndexToNotNullSectors(newIndex) {
    let foundSectorIndex = -1;
    let lastNotNullSectorIndex = -1;
    for (let sectorIndex = 0; sectorIndex < this.notNullSectors.length; sectorIndex++) {
      if (this.notNullSectors[sectorIndex].begin >= newIndex && newIndex <= this.notNullSectors[sectorIndex].end) {
        foundSectorIndex = sectorIndex;
        break;
      } else if (newIndex == this.notNullSectors[sectorIndex].end + 1) {
        lastNotNullSectorIndex = sectorIndex;
      }
    }
    if (foundSectorIndex < 0) {
      if (lastNotNullSectorIndex < 0) {
        this.notNullSectors.push({
          begin: newIndex,
          end: newIndex
        });
        return this.notNullSectors.length - 1;
      } else {
        this.notNullSectors[lastNotNullSectorIndex].end = newIndex;
        return lastNotNullSectorIndex;
      }
    }
    return foundSectorIndex;
  }
}
